import { useQuery } from '@tanstack/react-query';
import { CAPABILITIES_STALE_MS, getCapabilities } from '@/lib/api/capabilities';
import { keys } from '@/lib/query/keys';
import type { Locale } from '@/lib/api/types';

/**
 * Parse a locale list (`brand.locales`, or the comma-separated `VITE_LOCALES`) into the
 * console's Locale union. Anything unknown is dropped; nothing usable gives `null`, so the
 * caller decides the fallback rather than this guessing one.
 */
export function parseLocales(v: unknown): Locale[] | null {
  const raw = typeof v === 'string' ? v.split(',').map((s) => s.trim()) : v;
  if (!Array.isArray(raw)) return null;
  const out = [...new Set(raw.filter((x): x is Locale => x === 'en' || x === 'ar'))];
  return out.length > 0 ? out : null;
}

/**
 * The locales the console assumes until the server has said otherwise: the build-time
 * `VITE_LOCALES` (default `en,ar`), and never English alone. A capabilities request that is
 * slow or failing must not collapse every Arabic input on a bilingual site — the defining
 * risk of reading locales from the server (plan svet-parity §9.3 #1).
 */
export const FALLBACK_LOCALES: readonly Locale[] = parseLocales(import.meta.env.VITE_LOCALES) ?? [
  'en',
  'ar',
];

export interface ServerLocales {
  /** The content locales every EN/AR control renders. */
  locales: readonly Locale[];
  isBilingual: boolean;
  /** Whether publish refuses a record marked Arabic-incomplete (`publish_gate`). */
  requireArComplete: boolean;
  /**
   * Whether EN/AR chrome (AR column, "Missing AR" filter, Arabic-complete switch) belongs on
   * screen: the site serves Arabic, or the publish gate demands the Arabic flag — hiding the
   * switch under a live gate would leave nobody able to publish.
   */
  showArChrome: boolean;
  /** The server configuration could not be read; the fallback above is in use. */
  failed: boolean;
}

/**
 * The deployment's content locales and publish gate, from `GET /admin/capabilities`.
 * While that loads, or if it fails, {@link FALLBACK_LOCALES} applies and the gate is assumed
 * on (the server enforces it either way; assuming it off would hide the Arabic switch).
 */
export function useServerLocales(): ServerLocales {
  const caps = useQuery({
    queryKey: keys.capabilities(),
    queryFn: getCapabilities,
    staleTime: CAPABILITIES_STALE_MS,
    retry: 1,
  });
  const locales = parseLocales(caps.data?.brand?.locales) ?? FALLBACK_LOCALES;
  const isBilingual = locales.includes('ar');
  const requireArComplete = caps.data ? caps.data.publish_gate?.require_ar_complete === true : true;
  return {
    locales,
    isBilingual,
    requireArComplete,
    showArChrome: isBilingual || requireArComplete,
    failed: caps.isError,
  };
}
